'use client';
import { useEffect, useRef, useState } from 'react';
import type { MergedAchievement } from '../hooks/useMyAchievements';
import { AchievementIcon } from './AchievementIcon';
import { pickDescription, pickTitle, useAchievementLang } from '../utils/pickLocale';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { cn } from '@/utils/cn';
import { formatDateNumeric } from '@/i18n/formatDate';
import claimTranslations from './AchievementClaimBanner.translations';
import screenTranslations from '../AchievementsScreen.translations';

interface Props {
  achievement: MergedAchievement;
  onOpen: (key: string) => void;
  motion?: boolean;
  settle?: { delayMs: number; pop: boolean };
  viewerIsOwner?: boolean;
}

export function AchievementCard({ achievement, onOpen, motion = false, settle, viewerIsOwner = true }: Props) {
  const { t } = useLocalTranslation(claimTranslations);
  const { t: tScreen, lang: uiLang } = useLocalTranslation(screenTranslations);
  const locked = !achievement.unlockedAt;
  const unclaimed = !locked && !achievement.rewardClaimed;

  const wasUnclaimed = useRef(unclaimed);
  const [settling, setSettling] = useState(false);
  useEffect(() => {
    if (wasUnclaimed.current && !unclaimed && settle?.pop) setSettling(true);
    wasUnclaimed.current = unclaimed;
  }, [unclaimed, settle]);

  const lang = useAchievementLang();
  const title = pickTitle(achievement.i18n, lang);
  const description = pickDescription(achievement.i18n, lang);
  return (
    <button
      type="button"
      onClick={() => onOpen(achievement.key)}
      data-achievement={achievement.key}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget) setSettling(false);
      }}
      className={`relative flex flex-col items-center gap-2 overflow-hidden rounded-2xl bg-fill-tertiary p-3 text-center transition active:opacity-80 ${settling ? 'animate-shot-claim-settle' : ''}`}
      style={settling ? { animationDelay: `${settle?.delayMs ?? 0}ms` } : undefined}
    >

      {!locked && viewerIsOwner && (
        <div
          aria-hidden
          data-corner={achievement.key}
          className={`pointer-events-none absolute end-0 top-0 z-20 h-0 w-0 border-l-[18px] border-t-[18px] border-l-transparent ${motion ? 'shot-claim-corner' : ''} ${unclaimed ? 'border-t-price' : 'border-t-accent-green'}`}
          style={settle ? { transitionDelay: `${settle.delayMs}ms` } : undefined}
        />
      )}

      <div className={cn('pointer-events-none absolute inset-0 scale-[1.5] blur-[20px]', locked ? 'opacity-15' : 'opacity-35')}>
        <AchievementIcon iconFile={achievement.iconFile} alt="" fill className="object-contain" />
      </div>

      <div className={cn('relative z-10 aspect-square w-full', locked && 'grayscale opacity-45')}>
        <AchievementIcon iconFile={achievement.iconFile} alt={title} fill className="object-contain" />
      </div>
      <div className="relative z-10">
        <div className="text-sm font-medium text-label-primary">{title}</div>
        <div className="text-xs text-label-secondary line-clamp-2">{description}</div>
        {locked && achievement.progress && (
          <div className="mt-1 text-xs text-label-secondary">
            {achievement.progress.value} / {achievement.progress.threshold}
          </div>
        )}
        {unclaimed && viewerIsOwner && (
          <div className="mt-1 text-xs font-medium text-price">{t('notClaimed')}</div>
        )}
        {!viewerIsOwner && achievement.unlockedAt && (
          <div className="mt-1 text-xs text-label-tertiary">
            {tScreen('unlockedAt', { date: formatDateNumeric(achievement.unlockedAt, uiLang) })}
          </div>
        )}
      </div>
    </button>
  );
}
